import { Alert } from '@startuptools/ui/Alert';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import { Link } from '@startuptools/ui/Link';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import {
  useActiveCompanyStripeSubSuspenseQuery,
  type GqlWarrantProgram,
} from '../../../../../graphql/react-operations';
import { Controller, useForm } from 'react-hook-form';
import { useUpdateWarrantProgramMutation } from '../../warrant-programs.graphql';
import { Trans } from '@lingui/react';
import React from 'react';
import { WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import { GqlCompany } from '../../../../../graphql/base-types.graphql';

type FormData = Pick<GqlWarrantProgram, 'subscriberAccess'>;

export const WpStepOtherSettings = ({
  warrantProgram,
}: {
  warrantProgram: Pick<GqlWarrantProgram, 'id' | 'companyId' | 'subscriberAccess'> & {
    company: Pick<GqlCompany, 'isDemo'>;
  };
}) => {
  const {
    data: { activeCompanyStripeSubscription },
  } = useActiveCompanyStripeSubSuspenseQuery({
    variables: { companyId: warrantProgram.companyId },
    fetchPolicy: 'no-cache',
  });

  const hasActiveProSubscription = activeCompanyStripeSubscription?.activeProduct?.name.includes('PRO');

  const [updateWp, { loading }] = useUpdateWarrantProgramMutation({
    refetchQueries: [WarrantProgramDocument],
    awaitRefetchQueries: true,
  });

  const [success, setSuccess] = React.useState(false);

  React.useEffect(() => {
    if (success) {
      const id = setTimeout(() => setSuccess(false), 1000);
      return () => clearTimeout(id);
    }
  }, [success]);

  const { control, handleSubmit } = useForm<FormData>({
    defaultValues: {
      subscriberAccess: warrantProgram.subscriberAccess ?? (hasActiveProSubscription || warrantProgram.company.isDemo),
    },
  });

  const onSubmit = async (data: FormData) => {
    await updateWp({ variables: { companyId: warrantProgram.companyId, input: { ...data, id: warrantProgram.id } } });
    setSuccess(true);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Other settings" />
        </Typography>
        {!hasActiveProSubscription && (
          <Alert color="neutral">
            <Trans id="You need an active Pro subscription to enable the subscriber login feature." />
          </Alert>
        )}
        <FormControl>
          <Controller
            name="subscriberAccess"
            control={control}
            render={({ field: { value, ...props } }) => (
              <Checkbox
                {...props}
                checked={value}
                disabled={!warrantProgram.company.isDemo && !hasActiveProSubscription}
                onChange={e => {
                  return props.onChange(e.target.checked);
                }}
                label={<Trans id="Enable subscriber login" />}
              />
            )}
          />
          <FormHelperText slotProps={{ root: { sx: { display: 'block' } } }}>
            <Trans
              id="Subscribers can login on <link>my.bolago.app</link>  to view documents, the vesting schedule and current value of their warrants."
              components={{
                link: <Link href="https://my.bolago.app" />,
              }}
            />
          </FormHelperText>
        </FormControl>
        <Box>
          <Button type="submit" loading={loading} color={success ? 'success' : 'primary'}>
            <Trans id="Save" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
